import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, LifeBuoy, LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  changeEmailSchema,
  changePasswordSchema,
  getPasswordStrength,
} from "@/lib/support-center";
import { cn } from "@/lib/utils";

type SecurityEventRow = {
  id: string;
  event_type: string;
  status: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
};

const formatEventLabel = (value: string) =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatEventStatus = (value: string) =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const panelAccent = {
  email: "border-primary/30 bg-primary/10",
  password: "border-accent/30 bg-accent/10",
} as const;

const AccountSecurity = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, reauthenticate, updateEmail, updatePassword, refreshUser } = useAuth();

  const [emailForm, setEmailForm] = useState({
    newEmail: "",
    confirmEmail: "",
    currentPassword: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [events, setEvents] = useState<SecurityEventRow[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const activePanel = searchParams.get("panel") === "password" ? "password" : "email";
  const passwordStrength = useMemo(() => getPasswordStrength(passwordForm.newPassword), [passwordForm.newPassword]);

  useEffect(() => {
    if (!user) return;

    const loadEvents = async () => {
      setLoadingEvents(true);
      const { data, error } = await supabase
        .from("account_security_events")
        .select("id, event_type, status, created_at, metadata")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(6);

      if (error) {
        toast.error("Unable to load recent security activity.");
      } else {
        setEvents((data as SecurityEventRow[]) || []);
      }
      setLoadingEvents(false);
    };

    void loadEvents();
  }, [user]);

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user?.email) {
      toast.error("No active account email found.");
      return;
    }

    const parsed = changeEmailSchema.safeParse(emailForm);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || "Review your email details and try again.");
      return;
    }

    if (parsed.data.newEmail.toLowerCase() === user.email.toLowerCase()) {
      toast.error("Choose a different email address.");
      return;
    }

    setEmailSubmitting(true);
    setEmailSuccess(null);

    const reauthResult = await reauthenticate(parsed.data.currentPassword);
    if (reauthResult.error) {
      setEmailSubmitting(false);
      toast.error("Your current password was incorrect.");
      return;
    }

    const { error } = await updateEmail(parsed.data.newEmail);
    if (error) {
      setEmailSubmitting(false);
      toast.error(error.message);
      return;
    }

    await supabase.from("account_security_events").insert({
      user_id: user.id,
      event_type: "email_change_requested",
      status: "verification_pending",
      metadata: {
        previous_email: user.email,
        requested_email: parsed.data.newEmail,
        page_path: window.location.pathname,
      },
    });

    await refreshUser();
    setEmailSubmitting(false);
    setEmailForm({ newEmail: "", confirmEmail: "", currentPassword: "" });
    setEmailSuccess("Email update requested. If verification is required, check both your old and new inboxes to finish the change.");
    toast.success("Email change request submitted.");
  };

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) {
      toast.error("You need to be signed in.");
      return;
    }

    const parsed = changePasswordSchema.safeParse(passwordForm);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || "Review your password details and try again.");
      return;
    }

    setPasswordSubmitting(true);
    setPasswordSuccess(null);

    const reauthResult = await reauthenticate(parsed.data.currentPassword);
    if (reauthResult.error) {
      setPasswordSubmitting(false);
      toast.error("Your current password was incorrect.");
      return;
    }

    const { error } = await updatePassword(parsed.data.newPassword);
    if (error) {
      setPasswordSubmitting(false);
      toast.error(error.message);
      return;
    }

    await supabase.from("account_security_events").insert({
      user_id: user.id,
      event_type: "password_changed",
      status: "completed",
      metadata: {
        page_path: window.location.pathname,
        updated_at: new Date().toISOString(),
      },
    });

    setPasswordSubmitting(false);
    setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordSuccess("Your password has been updated successfully. Use the new password the next time you sign in.");
    toast.success("Password updated successfully.");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="container mx-auto flex max-w-5xl items-center gap-3 px-4 py-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/profile")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Account settings</p>
            <h1 className="font-heading text-xl font-bold text-foreground">Login & Security</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-5xl space-y-6 px-4 py-6">
        <section className="glass-card p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl space-y-3">
              <p className="text-sm uppercase tracking-[0.22em] text-muted-foreground">Security center</p>
              <h2 className="font-heading text-3xl font-bold text-foreground">Manage your credentials with clear, trusted controls.</h2>
              <p className="text-sm leading-7 text-muted-foreground">
                Update your email, refresh your password, and review recent account security activity from one place.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-3xl border border-border/70 bg-background/50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Current email</p>
                <p className="mt-2 break-all text-sm font-medium text-foreground">{user?.email || "Unavailable"}</p>
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/50 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Recovery support</p>
                <p className="mt-2 text-sm text-muted-foreground">Forgotten your password? Use guided recovery or contact support for access issues.</p>
              </div>
            </div>
          </div>
        </section>

        <div className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
          <section className="space-y-6">
            <div className={cn("glass-card p-6", activePanel === "email" && panelAccent.email)}>
              <div className="mb-5 flex items-start gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-primary/30 bg-primary/10 text-primary">
                  <Mail className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground">Change email address</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Confirm your current password, enter your new email, and we’ll handle the secure update flow.</p>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleEmailSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="current-email">Current email</Label>
                    <Input id="current-email" value={user?.email || ""} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-email">New email</Label>
                    <Input
                      id="new-email"
                      type="email"
                      autoComplete="email"
                      value={emailForm.newEmail}
                      onChange={(event) => setEmailForm((current) => ({ ...current, newEmail: event.target.value }))}
                      placeholder="new-email@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="confirm-email">Confirm new email</Label>
                    <Input
                      id="confirm-email"
                      type="email"
                      autoComplete="email"
                      value={emailForm.confirmEmail}
                      onChange={(event) => setEmailForm((current) => ({ ...current, confirmEmail: event.target.value }))}
                      placeholder="Repeat your new email"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email-current-password">Current password</Label>
                    <Input
                      id="email-current-password"
                      type="password"
                      autoComplete="current-password"
                      value={emailForm.currentPassword}
                      onChange={(event) => setEmailForm((current) => ({ ...current, currentPassword: event.target.value }))}
                      placeholder="Enter your current password"
                      required
                    />
                  </div>
                </div>

                {emailSuccess && (
                  <div className="rounded-2xl border border-primary/30 bg-primary/10 p-4 text-sm text-foreground">
                    {emailSuccess}
                  </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button type="submit" disabled={emailSubmitting}>
                    {emailSubmitting ? "Updating email..." : "Request email change"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => navigate("/support?category=account_issue")}>
                    Need account help
                  </Button>
                </div>
              </form>
            </div>

            <div className={cn("glass-card p-6", activePanel === "password" && panelAccent.password)}>
              <div className="mb-5 flex items-start gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-accent/30 bg-accent/10 text-accent">
                  <LockKeyhole className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground">Change password</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Choose a strong new password and verify your current one before we apply the change.</p>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handlePasswordSubmit}>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2 md:col-span-1">
                    <Label htmlFor="current-password">Current password</Label>
                    <Input
                      id="current-password"
                      type="password"
                      autoComplete="current-password"
                      value={passwordForm.currentPassword}
                      onChange={(event) => setPasswordForm((current) => ({ ...current, currentPassword: event.target.value }))}
                      placeholder="Current password"
                      required
                    />
                  </div>
                  <div className="space-y-2 md:col-span-1">
                    <Label htmlFor="new-password">New password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      autoComplete="new-password"
                      value={passwordForm.newPassword}
                      onChange={(event) => setPasswordForm((current) => ({ ...current, newPassword: event.target.value }))}
                      placeholder="New secure password"
                      required
                    />
                  </div>
                  <div className="space-y-2 md:col-span-1">
                    <Label htmlFor="confirm-password">Confirm new password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      autoComplete="new-password"
                      value={passwordForm.confirmPassword}
                      onChange={(event) => setPasswordForm((current) => ({ ...current, confirmPassword: event.target.value }))}
                      placeholder="Confirm new password"
                      required
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-border/70 bg-background/60 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">Password strength</span>
                    <span className="font-medium text-foreground">{passwordStrength.label}</span>
                  </div>
                  <Progress value={passwordStrength.score} className="h-2" />
                  <p className="text-xs leading-6 text-muted-foreground">Use at least 8 characters with uppercase and lowercase letters plus a number.</p>
                </div>

                {passwordSuccess && (
                  <div className="rounded-2xl border border-primary/30 bg-primary/10 p-4 text-sm text-foreground">
                    {passwordSuccess}
                  </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button type="submit" disabled={passwordSubmitting}>
                    {passwordSubmitting ? "Updating password..." : "Update password"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => navigate("/forgot-password") }>
                    Forgot password instead?
                  </Button>
                </div>
              </form>
            </div>
          </section>

          <aside className="space-y-6">
            <section className="glass-card p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-primary/30 bg-primary/10 text-primary">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground">Security activity</h2>
                  <p className="text-sm text-muted-foreground">Recent changes and recovery events tied to your account.</p>
                </div>
              </div>

              <div className="space-y-3">
                {loadingEvents ? (
                  <div className="rounded-2xl border border-border/70 bg-background/50 p-4 text-sm text-muted-foreground">Loading recent security events...</div>
                ) : events.length === 0 ? (
                  <div className="rounded-2xl border border-border/70 bg-background/50 p-4 text-sm text-muted-foreground">No recent security events recorded yet.</div>
                ) : (
                  events.map((securityEvent) => (
                    <div key={securityEvent.id} className="rounded-2xl border border-border/70 bg-background/50 p-4 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-foreground">{formatEventLabel(securityEvent.event_type)}</p>
                          <p className="text-xs text-muted-foreground">{new Date(securityEvent.created_at).toLocaleString()}</p>
                        </div>
                        <span className="rounded-full border border-border/70 bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                          {formatEventStatus(securityEvent.status)}
                        </span>
                      </div>
                      {typeof securityEvent.metadata?.requested_email === "string" && (
                        <p className="text-xs text-muted-foreground">Requested email: <span className="text-foreground">{securityEvent.metadata.requested_email}</span></p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="glass-card p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-accent/30 bg-accent/10 text-accent">
                  <LifeBuoy className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground">Need help?</h2>
                  <p className="text-sm text-muted-foreground">Use the fastest path for access issues or security concerns.</p>
                </div>
              </div>

              <div className="space-y-3">
                <Button className="w-full justify-start" onClick={() => navigate("/support?category=login_password_issue")}>
                  Login / password support
                </Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => navigate("/support?category=account_issue")}>
                  Account support
                </Button>
                <Button className="w-full justify-start" variant="outline" onClick={() => navigate("/feedback?type=ux_ui_suggestion")}>
                  Suggest a security UX improvement
                </Button>
                <Button className="w-full justify-start" variant="ghost" asChild>
                  <Link to="/forgot-password">
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Open password reset flow
                  </Link>
                </Button>
              </div>
            </section>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default AccountSecurity;
